// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { Component, Inject } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { firstValueFrom } from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { KdfRequest } from "@bitwarden/common/models/request/kdf.request";
import { PreloginRequest } from "@bitwarden/common/models/request/prelogin.request";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { MessagingService } from "@bitwarden/common/platform/abstractions/messaging.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { DIALOG_DATA, ToastService } from "@bitwarden/components";
import { KdfConfig, KdfConfigService, KdfType, KeyService } from "@bitwarden/key-management";

@Component({
  selector: "app-change-kdf-confirmation",
  templateUrl: "change-kdf-confirmation.component.html",
  standalone: false,
})
export class ChangeKdfConfirmationComponent {
  kdfConfig: KdfConfig;

  form = new FormGroup({
    masterPassword: new FormControl(null, Validators.required),
  });
  showPassword = false;
  masterPassword: string;
  loading = false;

  constructor(
    private apiService: ApiService,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private keyService: KeyService,
    private messagingService: MessagingService,
    @Inject(DIALOG_DATA) params: { kdf: KdfType; kdfConfig: KdfConfig },
    private accountService: AccountService,
    private toastService: ToastService,
    private kdfConfigService: KdfConfigService,
  ) {
    this.kdfConfig = params.kdfConfig;
    this.masterPassword = null;
  }

  submit = async () => {
    if (this.form.invalid) {
      return;
    }
    this.loading = true;
    await this.makeKeyAndSaveAsync();
    this.toastService.showToast({
      variant: "success",
      title: this.i18nService.t("encKeySettingsChanged"),
      message: this.i18nService.t("logBackIn"),
    });
    this.messagingService.send("logout");
    this.loading = false;
  };

  private async makeKeyAndSaveAsync() {
    const masterPassword = this.form.value.masterPassword;

    // Ensure the KDF config is valid.
    this.kdfConfig.validateKdfConfigForSetting();

    const request = new KdfRequest();
    request.kdf = this.kdfConfig.kdfType;
    request.kdfIterations = this.kdfConfig.iterations;
    if (this.kdfConfig.kdfType === KdfType.Argon2id) {
      request.kdfMemory = this.kdfConfig.memory;
      request.kdfParallelism = this.kdfConfig.parallelism;
    }

    const account = await firstValueFrom(this.accountService.activeAccount$);
    const email = account?.email;

    // Fyin pepper-strengthened PIN: fold the pepper into every makeMasterKey()
    // input so the derived MK matches peppered login. hashMasterKey() still uses
    // the raw PIN (PasswordLoginStrategy convention). We deliberately do NOT use
    // getOrDeriveMasterKey for the proof: on a cache miss (after a lock/unlock,
    // MASTER_KEY clears on lock) it derives from the raw PIN, so the proof hash
    // would not match the server for a migrated user. Deriving explicitly with
    // the CURRENT KDF config is correct whether or not the key is cached.
    const fyinPepper = await this.fyinFetchPepperIfMigrated(email);
    const passwordForKdf = fyinPepper != null ? masterPassword + fyinPepper : masterPassword;

    const currentKdfConfig = await this.kdfConfigService.getKdfConfig(account.id);
    const masterKey = await this.keyService.makeMasterKey(passwordForKdf, email, currentKdfConfig);
    request.masterPasswordHash = await this.keyService.hashMasterKey(masterPassword, masterKey);

    // New master key uses the NEW KDF config (this.kdfConfig) but the same
    // peppered KDF input so peppered login can re-derive it.
    const newMasterKey = await this.keyService.makeMasterKey(
      passwordForKdf,
      email,
      this.kdfConfig,
    );
    request.newMasterPasswordHash = await this.keyService.hashMasterKey(
      masterPassword,
      newMasterKey,
    );
    const newUserKey = await this.keyService.encryptUserKeyWithMasterKey(newMasterKey);
    request.key = newUserKey[1].encryptedString;

    await this.apiService.postAccountKdf(request);
  }

  /**
   * Fyin pepper-strengthened PIN: returns the pepper string for a migrated user
   * (so callers can derive MK from `pin || pepper` rather than `pin` alone).
   * Returns null for unmigrated users / errors / pepper-disabled servers; callers
   * fall back to the legacy KDF in that case.
   */
  private async fyinFetchPepperIfMigrated(email: string): Promise<string | null> {
    try {
      const preloginResponse = await this.apiService.postPrelogin(new PreloginRequest(email));
      if (preloginResponse?.pepperRequired === true && typeof preloginResponse.pepper === "string") {
        return preloginResponse.pepper;
      }
    } catch (e) {
      // 404 / network / etc — fall back to legacy KDF
    }
    return null;
  }
}
